import { NgModule, isDevMode, APP_INITIALIZER } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';

import { AppRoutingModule } from './route/app-routing.module';
import { AppComponent } from './app.component';
import { SidebarComponent } from './components/common/sidebar/sidebar.component';
import { CommonLayoutComponent } from './components/common/common-layout/common-layout.component';
import { LoginComponent } from './components/common/login/login.component';
import { SharedModule } from './components/shared/shared.module';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AddUserComponent } from './components/organizationAdmin/add-user/add-user.component';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { ApiInterceptorInterceptor } from './api-interceptor.interceptor';
import { NgxPaginationModule } from 'ngx-pagination';
import { ForgetPasswordComponent } from './components/common/forget-password/forget-password.component';
import { OtpComponent } from './components/common/otp/otp.component';
import { logGuardGuard } from './guards/log-guard.guard';
import { UnAuthorisedComponent } from './components/un-authorised/un-authorised.component';
import { PageNotFoundComponent } from './components/page-not-found.component';
import { TreeModule } from 'primeng/tree';
import { DatePipe } from '@angular/common';
import { NgOptimizedImage } from '@angular/common';
import { CommonService } from './services/common/common.service';
import { Router } from '@angular/router';
import { SideBarService } from './services/common/side-bar.service';
import { firstValueFrom } from 'rxjs';
import { MessageService } from './services/message.service';

// Function to load the theme before Angular boots
export function loadTheme(commonService: CommonService) {
  let theme = ['default', 'custom'];
  return () => commonService.loadThemeCSS(theme[1]); // or dynamically determine the theme
}

export function accessKeyLoginInitializer(
  commonService: CommonService,
  router: Router,
  sidebarService: SideBarService,
  messageService: MessageService
): () => Promise<void> {
  commonService.showSimpleLoader();
  return () => {
    const accessKey = new URLSearchParams(window.location.search).get(
      'accessKey'
    );

    if (!accessKey) {
      commonService.hideSimpleLoader();
      return Promise.resolve(); // no access key in URL
    }

    return firstValueFrom(commonService.loginUserWithAccessKey(accessKey))
      .then((res: any) => {
        commonService.removeLocalStorageData();
        commonService.userLoggedIn.next(false);
        let userData = {
          ...res?.data,
        };

        localStorage.setItem('logoImg', userData?.logo);
        commonService._logoImgPath.next(userData?.logo);
        localStorage.setItem('userData', JSON.stringify(userData));
        localStorage.setItem('authToken', userData.accessToken);
        localStorage.setItem(
          'appVersion',
          userData?.angularAppVersion[0]?.version
        );
        commonService.userLoggedIn.next(true);
        let validRoute = userData.userAccessModule;

        localStorage.setItem('userAccessModule', JSON.stringify(validRoute));

        const sidebarMenu = sidebarService.generateSidebar(validRoute);

        commonService.userMenu = sidebarMenu;

        localStorage.setItem('validRoutes', JSON.stringify(sidebarMenu));

        commonService.isStandardScreenModelOpen = true;

        // Navigate to dashboard if needed
        if (router.url === '/' || router.url.startsWith('/login')) {
          router.navigate(['/dashboard']);
        }

        return;
      })
      .catch(err => {
        // messageService.errorToast(
        //   'Failed to authenticate. The access key may be invalid or expired.'
        // );
        router.navigate(['/login']);
      })
      .finally(() => {
        commonService.hideSimpleLoader();
      });
  };
}

@NgModule({
  declarations: [
    AppComponent,
    SidebarComponent,
    CommonLayoutComponent,
    LoginComponent,
    AddUserComponent,
    ForgetPasswordComponent,
    OtpComponent,
    UnAuthorisedComponent,
    PageNotFoundComponent,
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    SharedModule,
    BrowserAnimationsModule,
    NgxPaginationModule,
    TreeModule,
    NgOptimizedImage,
    // RecaptchaModule,
    // RecaptchaFormsModule,
  ],
  providers: [
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ApiInterceptorInterceptor,
      multi: true,
    },
    DatePipe,
    {
      provide: APP_INITIALIZER,
      useFactory: loadTheme,
      deps: [CommonService],
      multi: true,
    },
    // 👇 New APP_INITIALIZER for accessKey login
    {
      provide: APP_INITIALIZER,
      useFactory: accessKeyLoginInitializer,
      deps: [CommonService, Router, SideBarService, MessageService],
      multi: true,
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
